import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Pagination,
  useMediaQuery,
} from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useParams, Outlet, useLocation } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import {
  fetchArchitecturalRequests,
  fetchStatuses,
  fetchRequestTypes,
} from "./Utils/archRequest.api";
import { getStatusInfo } from "./Utils/archRequest.utils";
import StatusFilterCards from "./StatusFilterCards";
import ArchitecturalRequestCard from "./ArchitecturalRequestsCards";
import ArchitecturalRequestTable from "./ArchitecturalRequestsTable";

const ArchitecturalRequestPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [viewMode, setViewMode] = useState("cards");
  const [page, setPage] = useState(1);
  const itemsPerPage = 18;

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location?.state?.reload) {
      loadData();
      window.history.replaceState({}, document.title);
    }
  }, [location?.state?.reload]);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, filterStatuses, startDate, endDate, requests]);

  const loadData = async () => {
    const [reqRes, statusRes, typesRes] = await Promise.all([
      fetchArchitecturalRequests(),
      fetchStatuses(),
      fetchRequestTypes(),
    ]);

    const data = reqRes.data;
    setRequests(data);
    setStatuses(statusRes.data);
    setRequestTypes(typesRes);

    const counts = {};
    for (const r of data) {
      const s = r.status.toLowerCase();
      counts[s] = (counts[s] || 0) + 1;
    }
    setStatusCounts(counts);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setStartDate(null);
    setEndDate(null);
    setPage(1);
  };

  const getRequestTypeName = (typeId) => {
    const found = requestTypes.find((t) => Number(t.id) === Number(typeId));
    return found?.type || "Unknown Type";
  };

  const applyFilters = () => {
    let results = [...requests];
    const terms = searchTerm.toLowerCase().split(",").map((s) => s.trim()).filter(Boolean);

    if (terms.length) {
      results = results.filter((r) => {
        const typeName = getRequestTypeName(r.request_type);
        const combined = `${r.id} ${r.property_id} ${typeName} ${r.status}`.toLowerCase();
        return terms.some(term => combined.includes(term));
      });
    }

    if (filterStatuses.length) {
      results = results.filter((r) => filterStatuses.includes(r.status));
    }

    if (startDate || endDate) {
      results = results.filter((r) => {
        const requestDate = new Date(r.requested_at);
        if (startDate && requestDate < new Date(startDate)) return false;
        if (endDate && requestDate > new Date(endDate)) return false;
        return true;
      });
    }

    setFiltered(results);
  };

  const handleStatusFilter = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  if (id) return <Outlet />;

  const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Title and View Toggle */}
      <Box
  sx={{
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    justifyContent: "space-between",
    alignItems: { xs: "flex-start", sm: "center" },
    gap: 2,
    mb: 3,
  }}
>
  <Typography variant="h4" fontWeight="bold">
    Architectural Requests
  </Typography>

  {/* Button group side-by-side */}
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      gap: 1,
      flexWrap: "wrap",
    }}
  >
    <Button
      variant="outlined"
      startIcon={
        viewMode === "cards" ? <TableChartIcon /> : <ViewModuleIcon />
      }
      onClick={() => setViewMode(viewMode === "cards" ? "table" : "cards")}
    >
      {viewMode === "cards" ? "Table View" : "Card View"}
    </Button>
    <Button
      variant="outlined"
      color="secondary"
      onClick={handleResetFilters}
    >
      Reset Filters
    </Button>
  </Box>
</Box>

      
    

      {/* Search & Date Range Filter */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <TextField
          fullWidth
          placeholder="Search by ID, Property, Type, or Status (comma-separated)"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={(newDate) => setStartDate(newDate)}
            slotProps={{ textField: { fullWidth: true } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={(newDate) => setEndDate(newDate)}
            slotProps={{ textField: { fullWidth: true } }}
          />
        </LocalizationProvider>
      </Box>

      {/* Filter Cards */}
      <StatusFilterCards
        statuses={statuses}
        statusCounts={statusCounts}
        activeFilters={filterStatuses}
        onToggle={handleStatusFilter}
        getStatusInfo={getStatusInfo}
      />

      {/* View Mode Section */}
      <Box sx={{ flexGrow: 1 }}>
        {viewMode === "cards" ? (
          <ArchitecturalRequestCard
            requests={paginated}
            getStatusDetails={(status) => getStatusInfo(statuses, status)}
          />
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <ArchitecturalRequestTable
              rows={filtered}
              getStatusInfo={getStatusInfo}
              statuses={statuses}
            />
          </Box>
        )}
      </Box>

      {/* Pagination */}
      {filtered.length > itemsPerPage && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={Math.ceil(filtered.length / itemsPerPage)}
            page={page}
            onChange={(e, val) => setPage(val)}
          />
        </Box>
      )}
    </Box>
  );
};

export default ArchitecturalRequestPage;
